import { BankRow, ReconciliationRowResult, ReconciliationExceptionCode } from '../types/fees';

/**
 * Small, self-contained CSV helpers for the reconciliation upload flow.
 *
 * Parsing supports the standard shape most Pakistani banks emit:
 *   - first line is a header row
 *   - values comma-separated
 *   - values may be double-quoted; embedded double-quotes escaped as ""
 *   - CRLF or LF line endings
 *   - UTF-8 BOM stripped
 *
 * Header column names are matched case-insensitively so a bursar doesn't
 * have to worry about "ChallanNumber" vs "challanNumber". The required
 * columns are enforced; anything extra is ignored.
 */

const REQUIRED_COLUMNS = ['challanNumber', 'amount', 'txnDate', 'bankRef'] as const;
const OPTIONAL_COLUMNS = ['memo', 'paidByName'] as const;
type ColumnKey = (typeof REQUIRED_COLUMNS)[number] | (typeof OPTIONAL_COLUMNS)[number];

const HEADER_ALIASES: Record<string, ColumnKey> = {
  'challannumber': 'challanNumber',
  'challan_number': 'challanNumber',
  'challan no': 'challanNumber',
  'challan_no': 'challanNumber',
  'voucher_number': 'challanNumber',
  'amount': 'amount',
  'paid_amount': 'amount',
  'deposit_amount': 'amount',
  'txndate': 'txnDate',
  'txn_date': 'txnDate',
  'transaction_date': 'txnDate',
  'date': 'txnDate',
  'bankref': 'bankRef',
  'bank_ref': 'bankRef',
  'bank_reference': 'bankRef',
  'reference': 'bankRef',
  'ref_no': 'bankRef',
  'reference_number': 'bankRef',
  'memo': 'memo',
  'note': 'memo',
  'notes': 'memo',
  'paidbyname': 'paidByName',
  'paid_by_name': 'paidByName',
  'paid_by': 'paidByName',
  'depositor': 'paidByName',
};

export interface CsvParseError {
  line: number;
  message: string;
}

export interface CsvParseResult {
  rows: BankRow[];
  errors: CsvParseError[];
  detectedColumns: string[];
}

/**
 * Parse one CSV line into fields, respecting double-quoted values that may
 * contain commas. Tiny hand-rolled state machine — CSV grammar is simple
 * enough that a full library isn't warranted for the bank-file shape.
 */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let buf = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') { buf += '"'; i++; }
        else { inQuotes = false; }
      } else {
        buf += c;
      }
    } else {
      if (c === ',') { out.push(buf); buf = ''; }
      else if (c === '"' && buf === '') { inQuotes = true; }
      else { buf += c; }
    }
  }
  out.push(buf);
  return out.map(v => v.trim());
}

export function parseReconciliationCsv(text: string): CsvParseResult {
  const errors: CsvParseError[] = [];
  const rows: BankRow[] = [];
  const withoutBom = text.replace(/^﻿/, '');
  const lines = withoutBom.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);

  if (lines.length === 0) {
    return { rows, errors: [{ line: 0, message: 'File is empty.' }], detectedColumns: [] };
  }
  if (lines.length === 1) {
    return {
      rows,
      errors: [{ line: 0, message: 'File has a header but no data rows.' }],
      detectedColumns: parseCsvLine(lines[0]),
    };
  }

  // Header parsing — case-insensitive, alias-aware.
  const rawHeader = parseCsvLine(lines[0]);
  const columnMap: Record<number, ColumnKey> = {};
  const detectedColumns: string[] = [];
  rawHeader.forEach((raw, idx) => {
    const key = HEADER_ALIASES[raw.toLowerCase().replace(/\s+/g, '_')];
    if (key) {
      columnMap[idx] = key;
      detectedColumns.push(key);
    }
  });

  const missingRequired = REQUIRED_COLUMNS.filter(c => !detectedColumns.includes(c));
  if (missingRequired.length > 0) {
    errors.push({
      line: 1,
      message: `Header is missing required column${missingRequired.length === 1 ? '' : 's'}: ${missingRequired.join(', ')}. Download the template for the expected shape.`,
    });
    return { rows, errors, detectedColumns };
  }

  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    const row: Partial<Record<ColumnKey, string>> = {};
    let anyValue = false;
    cells.forEach((val, idx) => {
      const key = columnMap[idx];
      if (key) {
        row[key] = val;
        if (val) anyValue = true;
      }
    });
    if (!anyValue) continue;   // stray blank line

    const amt = Number(String(row.amount ?? '').replace(/,/g, ''));
    rows.push({
      challanNumber: row.challanNumber ?? '',
      amount: Number.isFinite(amt) ? amt : String(row.amount ?? ''),
      txnDate: row.txnDate ?? '',
      bankRef: row.bankRef ?? '',
      memo: row.memo || undefined,
      paidByName: row.paidByName || undefined,
    });
  }

  return { rows, errors, detectedColumns };
}

/**
 * The canonical CSV template a bursar can download to see the expected
 * column shape. Includes two sample rows so it's obviously a template and
 * not a real batch.
 */
export function buildReconciliationTemplateCsv(): string {
  const header = 'challanNumber,amount,txnDate,bankRef,paidByName,memo';
  const sample = [
    '0001234567,12500,2026-09-15,HBL-TX-9982371,Ali Khan Sr.,September fee',
    '0009876543,8500,2026-09-15,HBL-TX-9982372,Sara Malik,September fee',
  ];
  return [header, ...sample].join('\r\n') + '\r\n';
}

/**
 * Serialize the exceptions from a reconciliation result into a CSV. Used by
 * the "Download exceptions" button so a bursar can chase them offline in
 * Excel without hand-copying anything.
 */
export function buildExceptionsCsv(rows: ReconciliationRowResult[]): string {
  const header = 'row,challanNumber,amount,student,outstanding,exceptionCode,message';
  const escape = (s: string | number | undefined) => {
    if (s == null) return '';
    const v = String(s);
    if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
    return v;
  };
  const lines = rows
    .filter(r => r.status === 'exception')
    .map(r => [
      r.index + 1,
      escape(r.challanNumber),
      escape(r.amount),
      escape(r.studentName),
      escape(r.outstanding),
      escape(r.exceptionCode),
      escape(r.exceptionMessage),
    ].join(','));
  return [header, ...lines].join('\r\n') + '\r\n';
}

/**
 * Force a browser download of a CSV string. Trivial in-browser trick — no
 * dependency needed.
 */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Human-readable label per exception code. Kept as one canonical map so the
 * FE never scatters copy across pages.
 */
export const EXCEPTION_LABEL: Record<ReconciliationExceptionCode, string> = {
  INVALID_CHALLAN_FORMAT: 'Invalid challan number',
  INVALID_AMOUNT:         'Invalid amount',
  INVALID_DATE:           'Invalid date',
  MISSING_BANK_REF:       'Missing bank reference',
  CHALLAN_NOT_FOUND:      'Challan not found',
  CHALLAN_CANCELLED:      'Challan cancelled',
  CHALLAN_WAIVED:         'Challan waived',
  ALREADY_PAID:           'Already paid',
  AMOUNT_MISMATCH:        'Amount mismatch',
  DUPLICATE_BANK_REF:     'Already reconciled',
  COMMIT_FAILED:          'Commit failed',
};
